import { DevTool } from '@hookform/devtools';
import { yupResolver } from '@hookform/resolvers/yup';
import { Place, PlanDataWhereToGoOptions } from 'api/client';
import AutoComplete from 'components/auto-complete';
import Button from 'components/button';
import RoutesList from 'components/route-list';
import React, { FC } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useUnmount } from 'react-use';
import { NavigatePlan } from 'types/base';
import { settings } from 'utils/config';
import { navigateWithDirection } from 'utils/helpers';
import * as yup from 'yup';
import { ReactComponent as PlusIcon } from '../../../assets/icons/icon-plus.svg';
import { ReactComponent as TrashIcon } from '../../../assets/icons/icon-trash.svg';
import FormControls from '../form-controls';
import {
  ButtonContentWrapper,
  ButtonWrapper,
  CardInnerWrapper,
  CardOuterWrapper,
  Description,
  IconButton,
  QuestionWrapper,
  Title,
} from '../question.styles';

type FormData = {
  places: {
    name: string;
    routes: { detail: string }[];
  }[];
};

const FormSchema = yup
  .object()
  .shape({
    places: yup
      .array()
      .of(
        yup
          .object()
          .shape({
            name: yup.string().nullable(),
            routes: yup.array().of(
              yup.object().shape({
                detail: yup.string().defined(),
              })
            ),
          })
          .defined()
      )
      .defined(),
  })
  .defined();

interface Props {
  whereToGoOptions?: PlanDataWhereToGoOptions;
  onSetWhereToGo: (whereToGoOptions: PlanDataWhereToGoOptions) => void;
  navigate?: NavigatePlan;
}

const defaultValuesNew: FormData = {
  places: [{ name: '', routes: [{ detail: '' }] }],
};

const WhereToGo: FC<Props> = (props) => {
  const {
    register,
    setValue,
    getValues,
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting, isDirty },
  } = useForm<FormData>({
    defaultValues: props.whereToGoOptions
      ? {
          places: props.whereToGoOptions?.places.map((place: Place) => {
            return {
              name: place.name ?? '',
              routes: !!place.routes?.length
                ? place.routes.map((r: string) => {
                    return { detail: `${r}` };
                  })
                : [{ detail: '' }],
            };
          }),
        }
      : defaultValuesNew,
    resolver: yupResolver(FormSchema),
  });

  const { fields: placesFields, remove } = useFieldArray({
    control,
    name: 'places',
  });

  const onSubmit = handleSubmit((whereToGo, event) => {
    const setPlaces = {
      places: whereToGo.places
        .map((place: any) => {
          return {
            name: place.name,
            routes: !!place.routes?.length
              ? place.routes
                  .map((route: any) => route.detail)
                  .filter((item: any, index: number) => item || index === 0)
              : [],
          };
        })
        .filter((item: any, index: number) => item.name || index === 0),
    };
    // <FreeText> requires an array of strings for each field
    // Given we are not validating inputs in order to let the user progress,
    // We just accept and store empty field values and validate in the plan summary.
    props.onSetWhereToGo(setPlaces);
    navigateWithDirection(props.navigate, event);
  });

  const formData = watch();
  useUnmount(() => {
    if (!isSubmitting && isDirty) {
      const { places } = formData;
      props.onSetWhereToGo({
        places: places
          .map((place: any) => {
            const returnVal = {
              name: place.name,
              routes: !!place.routes?.length
                ? place.routes
                    .map((route: any) => route.detail)
                    .filter((item: any, index: number) => item || index === 0)
                : [{ detail: '' }],
            };
            return returnVal;
          })
          .filter((item: any, index: number) => item.name || index === 0),
      });
    }
  });

  return (
    <form noValidate onSubmit={onSubmit}>
      <QuestionWrapper>
        <Title>Where will we go and how will we get there?</Title>

        <Description>
          Where are safe places you can drive to if you need to evacuate your
          property?
          <br />
          <br />
          Your usual route may be blocked due to bushfire. Choose two safe
          places you could go to in a bushfire. For each safe place, include two
          routes to get there.
        </Description>

        {placesFields.map((field, index) => (
          <CardOuterWrapper key={field.id}>
            {index !== 0 && (
              <IconButton
                type='button'
                aria-label='delete place'
                onClick={() => {
                  remove(index);
                }}
              >
                <TrashIcon aria-hidden />
              </IconButton>
            )}
            <CardInnerWrapper>
              <Controller
                control={control}
                name={`places[${index}].name`}
                defaultValue={field.name}
                render={({ onChange, value }) => (
                  <AutoComplete
                    label='Where will you go?'
                    address={value}
                    onChange={(address) => onChange(address)}
                  />
                )}
              />

              <RoutesList nestIndex={index} {...{ control, register }} />
            </CardInnerWrapper>
          </CardOuterWrapper>
        ))}

        <ButtonWrapper>
          <Button
            variant='dark'
            disabled={placesFields.length >= settings.maxNumOfItems}
            onClick={() => {
              setValue('places', [
                ...getValues().places,
                {
                  name: '',
                  routes: [{ detail: '' }],
                },
              ]);
            }}
          >
            <ButtonContentWrapper>
              <PlusIcon />
              Add another safe place
            </ButtonContentWrapper>
          </Button>
        </ButtonWrapper>

        <DevTool control={control} />
      </QuestionWrapper>
      <FormControls />
    </form>
  );
};

export default WhereToGo;
